/**
 * AgentSessionsListPage — AC-AGS-M4-5
 *
 * Session history page at /roros/agent-sessions (list view).
 * Existing /roros/agent-sessions/:id is the detail view from M1.
 *
 * Features:
 * - Paginated table: operator name, type chip, status chip, last activity, turns
 * - Filter by status; search by operator name
 * - RBAC: members see own sessions; admins/owners see all org sessions
 * - Row click → navigates to session detail
 * - Empty state: "No sessions yet."
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Clock, CheckCircle2, AlertCircle, Ban,
  Loader2, Search, ChevronLeft, ChevronRight, Plus,
} from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import type { AgentSessionDTO } from '@/services/roros/agent-sessions.service';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  idle:          { label: 'Idle',          icon: Clock,         className: 'bg-slate-100 text-slate-600' },
  running:       { label: 'Running',       icon: Loader2,       className: 'bg-blue-50 text-blue-700' },
  completed:     { label: 'Completed',     icon: CheckCircle2,  className: 'bg-green-50 text-green-700' },
  failed:        { label: 'Failed',        icon: AlertCircle,   className: 'bg-red-50 text-red-700' },
  cancelled:     { label: 'Cancelled',     icon: Ban,           className: 'bg-amber-50 text-amber-700' },
  waiting_child: { label: 'Waiting',       icon: Clock,         className: 'bg-purple-50 text-purple-700' },
  waiting_user:  { label: 'Waiting User',  icon: Clock,         className: 'bg-purple-50 text-purple-700' },
};

function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.idle;
  const Icon = cfg.icon;
  const isSpinning = status === 'running';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`} data-testid="session-status-chip">
      <Icon className={`h-3 w-3 ${isSpinning ? 'animate-spin' : ''}`} />
      {t(cfg.label)}
    </span>
  );
}

// ─── Session type chip ────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  interactive: { label: 'Interactive', className: 'bg-slate-100 text-slate-600' },
  delegated:   { label: 'Delegated',   className: 'bg-blue-100 text-blue-700' },
  scheduled:   { label: 'Scheduled',   className: 'bg-purple-100 text-purple-700' },
  chat:        { label: 'Chat',        className: 'bg-slate-100 text-slate-600' },
};

function TypeChip({ type }: { type: string }) {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.interactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`} data-testid="session-type-chip">
      {t(cfg.label)}
    </span>
  );
}

// ─── Relative time ────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return t('—');
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t('just now');
  if (minutes < 60) return `${minutes} ${t('min ago')}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${t('hours ago')}`;
  const days = Math.floor(hours / 24);
  return `${days} ${t('days ago')}`;
}

// ─── Filter options ───────────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: t('All') },
  { value: 'active', label: t('Active') },
  { value: 'completed', label: t('Completed') },
  { value: 'failed', label: t('Failed') },
  { value: 'cancelled', label: t('Cancelled') },
];

const PAGE_SIZE = 20;

// ─── Main page ────────────────────────────────────────────────────────────────

export function AgentSessionsListPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission(PERMISSIONS.ROROS_ACTORS_MANAGE);

  const [sessions, setSessions] = useState<AgentSessionDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  // AC-CHAT-8: title+content search (FIRST filter — sessions-list-search-input)
  const [contentSearch, setContentSearch] = useState('');
  const [debouncedContentSearch, setDebouncedContentSearch] = useState('');
  // Existing operator name search
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce content search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedContentSearch(contentSearch), 300);
    return () => clearTimeout(timer);
  }, [contentSearch]);

  // Debounce operator name search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch, debouncedContentSearch]);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await agentSessionsService.listAllSessions({
        all: isAdmin,
        status: statusFilter === 'all' ? null : statusFilter,
        actorName: debouncedSearch || null,
        search: debouncedContentSearch || null,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setSessions(result.items);
      setTotal(result.total);
    } catch (err: any) {
      setError(err?.message ?? t('Failed to load sessions'));
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, statusFilter, debouncedSearch, debouncedContentSearch, page]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <RorosPage
      title={t('Agent Sessions')}
      icon={<Bot className="h-5 w-5" aria-hidden />}
      actions={
        <Button onClick={() => navigate('/roros/agent-sessions/new')} data-testid="new-session-btn">
          <Plus className="h-4 w-4 mr-1.5" />
          {t('New Session')}
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Filters row — AC-CHAT-8: content search is FIRST */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* AC-CHAT-8: title + message content search — FIRST filter */}
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
              placeholder={t('Search by title or message content…')}
              className="pl-9"
              data-testid="sessions-list-search-input"
            />
          </div>
          {/* Operator name search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('Search by operator name…')}
              className="pl-9"
              data-testid="sessions-search"
            />
          </div>
          <div className="w-40">
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
              data-testid="sessions-status-filter"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="sessions-error">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" data-testid="sessions-loading" />
            </div>
          )}

          {!isLoading && sessions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="sessions-empty">
              <Bot className="h-10 w-10 mb-3 text-slate-300" />
              <p className="text-sm">{t('No sessions yet.')}</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/roros/agent-sessions/new')}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {t('New Session')}
              </Button>
            </div>
          )}

          {!isLoading && sessions.length > 0 && (
            <table className="w-full text-sm" data-testid="sessions-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left font-medium text-slate-600">{t('Operator')}</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">{t('Type')}</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">{t('Status')}</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">{t('Last Activity')}</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">{t('Turns')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr
                    key={session.id}
                    onClick={() => navigate(`/roros/agent-sessions/${session.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid="session-row"
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/roros/agent-sessions/${session.id}`);
                      }
                    }}
                    aria-label={`${t('Open session')} ${session.title ?? session.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-purple-500 flex-shrink-0" />
                        <span className="font-medium text-slate-800 truncate max-w-[200px]" data-testid="session-actor-name">
                          {(session as any).actorName ?? session.actorId ?? t('Unknown')}
                        </span>
                      </div>
                      {session.title && (
                        <p className="text-xs text-slate-500 truncate max-w-[200px] mt-0.5">{session.title}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <TypeChip type={session.sessionType} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={session.status ?? 'idle'} />
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {relativeTime(session.lastActivityAt ?? session.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {session.messageCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>{`${t('Showing')} ${Math.min((page - 1) * PAGE_SIZE + 1, total)}–${Math.min(page * PAGE_SIZE, total)} ${t('of')} ${total}`}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label={t('Previous page')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>{page} / {totalPages}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                aria-label={t('Next page')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </RorosPage>
  );
}
